import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import apiService from '../../../services/api';

const AccountStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.request('/users/stats');
        if (response.success) {
          setStats(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch stats');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching account stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Mock achievements data (not implemented in backend yet)
  const achievements = [
    {
      id: 'early-adopter',
      title: 'Early Adopter',
      description: 'Joined ExpenseLoop in the first year',
      icon: 'Star',
      earned: true
    },
    {
      id: 'social-butterfly',
      title: 'Social Butterfly',
      description: 'Split expenses with 25+ different people',
      icon: 'Users',
      earned: true
    },
    {
      id: 'settlement-master',
      title: 'Settlement Master',
      description: 'Settled all balances within 30 days',
      icon: 'Award',
      earned: false
    }
  ];

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Account Statistics</h3>
          <Icon name="BarChart3" size={20} className="text-muted-foreground" />
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Account Statistics</h3>
          <Icon name="BarChart3" size={20} className="text-muted-foreground" />
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <p>Unable to load statistics</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Format stats data
  const displayStats = stats?.stats ? [
    {
      id: 'events',
      label: 'Events Participated',
      value: stats.stats.events || 0,
      change: stats.monthly?.events ? `+${stats.monthly.events}` : 0,
      changeLabel: 'this month',
      icon: 'Calendar',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      id: 'expenses',
      label: 'Expenses Tracked',
      value: stats.stats.expenses || 0,
      change: stats.monthly ? `+${Math.round((stats.monthly.expenses || 0) / 10)}` : 0, // Approximate based on monthly amount
      changeLabel: 'this month',
      icon: 'DollarSign',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      id: 'settlements',
      label: 'Settlements Made',
      value: stats.stats.settlements || 0,
      change: +2, // Placeholder
      changeLabel: 'this month',
      icon: 'CreditCard',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      id: 'friends',
      label: 'Active Friends',
      value: stats.stats.friends || 0,
      change: +5, // Placeholder
      changeLabel: 'this month',
      icon: 'Users',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Account Statistics</h3>
          <Icon name="BarChart3" size={20} className="text-muted-foreground" />
        </div>

        <div className="space-y-4">
          {displayStats?.map((stat) => (
            <div key={stat?.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${stat?.bgColor}`}>
                  <Icon name={stat?.icon} size={16} className={stat?.color} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat?.label}</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-semibold text-foreground">{stat?.value}</span>
                    {stat?.change > 0 && (
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                        {stat?.change} {stat?.changeLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Progress Indicators */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Monthly Goal Progress</span>
                <span className="text-foreground font-medium">75%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary h-2 rounded-full w-3/4 transition-all duration-300"></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Settlement Rate</span>
                <span className="text-foreground font-medium">94%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '94%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Achievements</h3>
          <Icon name="Trophy" size={20} className="text-muted-foreground" />
        </div>

        <div className="space-y-3">
          {achievements?.map((achievement) => (
            <div
              key={achievement?.id}
              className={`p-3 rounded-lg border transition-all ${
                achievement?.earned
                  ? 'bg-muted border-border' :'bg-background border-dashed border-muted-foreground/30'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  achievement?.earned
                    ? 'bg-yellow-100' :'bg-gray-100'
                }`}>
                  <Icon
                    name={achievement?.icon}
                    size={16}
                    className={achievement?.earned ? 'text-yellow-600' : 'text-gray-400'}
                  />
                </div>
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${
                    achievement?.earned ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {achievement?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {achievement?.description}
                  </p>
                </div>
                {achievement?.earned && (
                  <Icon name="Check" size={16} className="text-green-600" />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            Complete more activities to unlock achievements
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountStats;
